import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const SimilarGifts = ({ currentProduct }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Use related products from API, fallback to empty array
  const similarGifts = currentProduct?.related_products || [];

  // Don't render if no similar gifts
  if (!similarGifts || similarGifts.length === 0) {
    return null;
  }

  const itemsToShow = 3;
  const maxIndex = Math.max(0, similarGifts.length - itemsToShow);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Icon
        key={index}
        name="Star"
        size={14}
        className={`${
          index < rating ? 'text-warning fill-current' : 'text-accent-300'
        }`}
      />
    ));
  };

  const getSuccessRateColor = (rate) => {
    if (rate >= 95) return 'text-success bg-success-50';
    if (rate >= 90) return 'text-warning bg-warning-50';
    return 'text-error bg-error-50';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-accent-800">
            Similar Gifts You'll Love
          </h2>
          <p className="text-accent-600 mt-1">
            Curated selections based on your interests
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={prevSlide}
            className="w-10 h-10 rounded-full bg-accent-100 hover:bg-accent-200 flex items-center justify-center transition-colors duration-200"
          >
            <Icon name="ChevronLeft" size={20} className="text-accent-600" />
          </button>
          <button
            onClick={nextSlide}
            className="w-10 h-10 rounded-full bg-accent-100 hover:bg-accent-200 flex items-center justify-center transition-colors duration-200"
          >
            <Icon name="ChevronRight" size={20} className="text-accent-600" />
          </button>
        </div>
      </div>

      {/* Gifts Carousel */}
      <div className="relative overflow-hidden">
        <div
          className="flex transition-transform duration-500 ease-in-out"
          style={{
            transform: `translateX(-${currentIndex * (100 / itemsToShow)}%)`,
            width: `${(similarGifts.length * 100) / itemsToShow}%`
          }}
        >
          {similarGifts.map((gift) => (
            <div
              key={gift.id}
              className="w-full px-2"
              style={{ width: `${100 / similarGifts.length}%` }}
            >
              <div className="bg-white rounded-2xl border border-accent-100 overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1 group">
                {/* Image */}
                <div className="relative bg-accent-50 overflow-hidden" style={{ height: '200px' }}>
                  <Image
                    src={gift.images?.[0]?.image || '/assets/images/no_image.png'}
                    alt={gift.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  
                  {/* Success Rate Badge - Hide if no data */}
                  {gift.success_rate && (
                    <div className="absolute top-3 left-3">
                      <div className={`px-2 py-1 rounded-full text-xs font-medium ${getSuccessRateColor(gift.success_rate)}`}>
                        {gift.success_rate}% success
                      </div>
                    </div>
                  )}

                  {/* Quick Actions */}
                  <div className="absolute top-3 right-3 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <button className="w-8 h-8 bg-white/90 rounded-full flex items-center justify-center shadow-soft hover:bg-white transition-colors duration-200">
                      <Icon name="Heart" size={14} className="text-accent-600" />
                    </button>
                    <button className="w-8 h-8 bg-white/90 rounded-full flex items-center justify-center shadow-soft hover:bg-white transition-colors duration-200">
                      <Icon name="Eye" size={14} className="text-accent-600" />
                    </button>
                  </div>

                  {/* Price Badge */}
                  <div className="absolute bottom-3 left-3">
                    <div className="bg-white/95 backdrop-blur-sm px-2 py-1 rounded-full">
                      <span className="font-bold text-accent-800">${gift.retail_price}</span>
                      {gift.discount_price && (
                        <span className="text-sm text-accent-500 line-through ml-1">
                          ${gift.discount_price}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4">
                  <div className="mb-2">
                    <p className="text-xs text-accent-600 font-medium mb-1">
                      {gift.brand}
                    </p>
                    <h3 className="font-heading font-semibold text-accent-800 leading-tight line-clamp-2">
                      {gift.name}
                    </h3>
                  </div>

                  {/* Rating */}
                  {gift.external_rating_average && (
                    <div className="flex items-center space-x-1 mb-3">
                      <div className="flex">
                        {renderStars(Math.floor(gift.external_rating_average))}
                      </div>
                      <span className="text-xs text-accent-600">
                        ({gift.external_rating_average})
                      </span>
                    </div>
                  )}

                  {/* Why It Works */}
                  <div className="mb-4">
                    <p className="text-xs text-accent-600 leading-relaxed">
                      {gift.gift_description || "A thoughtful gift that creates memorable moments."}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex space-x-2">
                    <Button
                      variant="primary"
                      size="sm"
                      className="flex-1"
                      iconName="ExternalLink"
                      onClick={() => {
                        if (gift.affiliate_link) {
                          window.open(gift.affiliate_link, '_blank');
                        } else {
                          console.log('No affiliate URL for gift:', gift.id);
                        }
                      }}
                    >
                      Buy Now
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="ArrowRight"
                      onClick={() => window.location.href = `/products/${gift.slug}`}
                      className="px-3"
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Indicators */}
      <div className="flex justify-center space-x-2">
        {Array.from({ length: maxIndex + 1 }).map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`w-2 h-2 rounded-full transition-colors duration-200 ${
              index === currentIndex ? 'bg-primary' : 'bg-accent-300'
            }`}
          />
        ))}
      </div>

      {/* Browse More */}
      <div className="text-center">
        <Button
          variant="outline"
          iconName="Search"
          iconPosition="left"
          onClick={() => console.log('Browse more gifts')}
        >
          Browse More Similar Gifts
        </Button>
      </div>
    </div>
  );
};

export default SimilarGifts;